/* =========================================================================
   SMELU CART DRAWER — Brand-Logic-Layer
   Hört auf Cart-Updates, aktualisiert Free-Shipping-Bar + Bundle-Promo-Bar,
   rendert AJAX-Cross-Sell-Karten + bucket-basierte Empfehlungen.
   Stand: 2026-05-13
   Abhängigkeiten: Refresh-Native cart.js / cart-drawer.js bleiben aktiv.
   ========================================================================= */

(function () {
  'use strict';

  // ===== Konfiguration (von Liquid via data-*-Attribute überschreibbar) =====
  const DEFAULTS = {
    fsThresholdCents: 8900,           // €89,00
    bundleTag: 'set-bundle-eligible',
    bundleMinQty: 2,
    bundleDiscountPercent: 30,
    currencyFormat: '€{{amount}}'
  };

  // ===== Money-Format (deutsche Notation €X,XX) =====
  function formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace('.', ',');
    return '€' + euros;
  }

  // ===== Read config from <smelu-cart-drawer-config> data attributes =====
  function readConfig() {
    const root = document.querySelector('[data-smelu-cd-config]');
    if (!root) return { ...DEFAULTS };
    return {
      fsThresholdCents: parseInt(root.dataset.fsThreshold || DEFAULTS.fsThresholdCents, 10),
      bundleTag: root.dataset.bundleTag || DEFAULTS.bundleTag,
      bundleMinQty: parseInt(root.dataset.bundleMinQty || DEFAULTS.bundleMinQty, 10),
      bundleDiscountPercent: parseInt(root.dataset.bundleDiscount || DEFAULTS.bundleDiscountPercent, 10)
    };
  }

  // ===== Cart-State holen =====
  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.json();
  }

  // ===== Free-Shipping-Bar updaten =====
  function updateFreeShippingBar(cart, config) {
    const bar = document.querySelector('[data-smelu-fs-bar]');
    if (!bar) return;

    const threshold = config.fsThresholdCents;
    const total = cart.total_price;
    const remaining = Math.max(0, threshold - total);
    const progress = Math.min(100, (total / threshold) * 100);
    const unlocked = remaining === 0;

    const wasUnlocked = bar.classList.contains('is-unlocked');

    const fill = bar.querySelector('[data-smelu-fs-fill]');
    const amount = bar.querySelector('[data-smelu-fs-amount]');
    const percent = bar.querySelector('[data-smelu-fs-percent]');
    const label = bar.querySelector('[data-smelu-fs-label]');
    const unlockedLabel = bar.querySelector('[data-smelu-fs-unlocked]');

    if (fill) fill.style.width = progress + '%';
    if (amount) amount.textContent = formatMoney(remaining);
    if (percent) percent.textContent = Math.round(progress) + '%';

    if (unlocked) {
      bar.classList.add('is-unlocked');
      if (label) label.style.display = 'none';
      if (unlockedLabel) unlockedLabel.style.display = '';
      if (!wasUnlocked) triggerConfetti(bar);
    } else {
      bar.classList.remove('is-unlocked');
      if (label) label.style.display = '';
      if (unlockedLabel) unlockedLabel.style.display = 'none';
    }
  }

  // ===== Bundle-Promo-Bar updaten =====
  // Auto-Detection: Tag set-bundle-eligible ODER Title-Match "Pfannenset"
  // (aber NICHT "Utensilien-Set" — das ist kein Pfannen-Set)
  function isBundleEligible(item, tag) {
    // Tag-Check (Override für Admins)
    const tags = (item.product_tags || item.tags || []);
    const tagArr = Array.isArray(tags)
      ? tags.map(t => String(t).toLowerCase())
      : String(tags).split(',').map(t => t.trim().toLowerCase());
    if (tagArr.includes(String(tag).toLowerCase())) return true;

    // Title-Match-Fallback: "Pfannenset" UND NICHT "Utensilien"
    const title = String(item.product_title || item.title || '').toLowerCase();
    if (title.includes('pfannenset') && !title.includes('utensilien')) return true;

    return false;
  }

  function countQualifyingSets(cart, tag) {
    let count = 0;
    cart.items.forEach(item => {
      if (isBundleEligible(item, tag)) {
        count += item.quantity;
      }
    });
    return count;
  }

  function updateBundleBar(cart, config) {
    const bar = document.querySelector('[data-smelu-bundle-bar]');
    if (!bar) return;

    // Verstecke Bar wenn Cart leer
    if (!cart.items || cart.items.length === 0) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = '';

    const count = countQualifyingSets(cart, config.bundleTag);
    const min = config.bundleMinQty;
    const remaining = Math.max(0, min - count);
    const unlocked = count >= min;

    const badge = bar.querySelector('[data-smelu-bundle-badge]');
    const lockedRow = bar.querySelector('[data-smelu-bundle-locked]');
    const progressRow = bar.querySelector('[data-smelu-bundle-progress]');
    const unlockedRow = bar.querySelector('[data-smelu-bundle-unlocked]');
    const savedEl = bar.querySelector('[data-smelu-bundle-saved]');

    bar.classList.remove('is-locked', 'is-progress', 'is-unlocked');

    if (unlocked) {
      bar.classList.add('is-unlocked');
      if (badge) badge.textContent = count + ' / ' + min + ' ✓';
      if (lockedRow) lockedRow.hidden = true;
      if (progressRow) progressRow.hidden = true;
      if (unlockedRow) unlockedRow.hidden = false;
      if (savedEl) {
        // Geschätzte Ersparnis: count × Discount auf Item-Preis ist komplex,
        // wir zeigen den tatsächlichen Discount aus cart.cart_level_discount_applications
        const discountTotal = (cart.cart_level_discount_applications || [])
          .reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
        savedEl.textContent = formatMoney(discountTotal);
      }
    } else if (count > 0) {
      bar.classList.add('is-progress');
      if (badge) badge.textContent = count + ' / ' + min;
      if (lockedRow) lockedRow.hidden = true;
      if (progressRow) {
        progressRow.hidden = false;
        const remainEl = progressRow.querySelector('[data-smelu-bundle-remaining]');
        if (remainEl) remainEl.textContent = remaining;
      }
      if (unlockedRow) unlockedRow.hidden = true;
    } else {
      bar.classList.add('is-locked');
      if (badge) badge.textContent = '0 / ' + min;
      if (lockedRow) lockedRow.hidden = false;
      if (progressRow) progressRow.hidden = true;
      if (unlockedRow) unlockedRow.hidden = true;
    }
  }

  // ===== Confetti einmalig triggern =====
  function triggerConfetti(host) {
    const existing = host.querySelector('.smelu-cd-confetti');
    if (existing) existing.remove();
    const c = document.createElement('div');
    c.className = 'smelu-cd-confetti';
    c.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 8; i++) c.appendChild(document.createElement('span'));
    host.appendChild(c);
    setTimeout(() => c.remove(), 1600);
  }

  // ===== AJAX-Add-to-Cart für Cross-Sell =====
  async function ajaxAddItem(variantId, cardEl) {
    if (cardEl) cardEl.classList.add('is-loading');
    try {
      const drawer = document.querySelector('cart-drawer');
      // Nutze Refresh-Native sections-Render für automatisches DOM-Update
      // FIX: CartDrawer.getSectionsToRender() liefert .id (nicht .section)
      const sections = drawer && drawer.getSectionsToRender
        ? drawer.getSectionsToRender().map(s => s.id).join(',')
        : 'cart-drawer,cart-icon-bubble';

      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!r.ok) throw new Error('Add failed');
      const result = await r.json();

      // Refresh-Native macht das Item-Re-Render via Section-API
      if (drawer && drawer.renderContents) {
        drawer.renderContents(result);
      } else {
        // Fallback: Reload
        window.location.reload();
        return;
      }

      // Success-Animation
      if (cardEl) {
        cardEl.classList.remove('is-loading');
        cardEl.classList.add('is-success');
        setTimeout(() => cardEl.classList.remove('is-success'), 800);
      }

      // Eigenes Event für Bar-Sync
      window.dispatchEvent(new CustomEvent('smelu:cart:updated'));
    } catch (e) {
      if (cardEl) cardEl.classList.remove('is-loading');
      console.warn('Smelu cart add failed', e);
    }
  }

  // ===== Cross-Sell-Karten initial verdrahten =====
  function bindCrossSellCards() {
    document.querySelectorAll('[data-smelu-cd-add]').forEach(btn => {
      if (btn.dataset.smeluBound === '1') return;
      btn.dataset.smeluBound = '1';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const card = btn.closest('.smelu-cd-card');
        // Wenn die Karte einen Variant-Picker hat, nimm die aktuelle Auswahl,
        // sonst Fallback auf das Button-Data-Attribut (Single-Variant-Produkt).
        let variantId = btn.dataset.smeluCdAdd;
        if (card) {
          const variantSelect = card.querySelector('[data-smelu-cd-variant]');
          if (variantSelect && variantSelect.value) {
            variantId = variantSelect.value;
          }
        }
        if (!variantId) return;
        await ajaxAddItem(parseInt(variantId, 10), card);
      });
    });
  }

  // ===== Cart-Update-Hook =====
  async function syncBars() {
    const config = readConfig();
    try {
      const cart = await fetchCart();
      updateFreeShippingBar(cart, config);
      updateBundleBar(cart, config);
    } catch (e) {
      console.warn('Smelu bar sync failed', e);
    }
  }

  // ===== PDP-ATC-Intercept: öffnet Drawer nach erfolgreichem Add =====
  // Greift bei jedem Form-Submit auf /cart/add (auch Custom-PDP-Forms ohne
  // <product-form>-Wrapper). Re-rendert via Refresh-Native getSectionsToRender().
  async function handleAtcSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      // Skip wenn Refresh-Native product-form-Wrapper bereits eingreift
      if (form.closest('product-form')) return;
      // Opt-Out
      if (form.dataset.smeluNoIntercept === '1') return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return; // dann passiert nativer Submit → /cart-Page

      e.preventDefault();
      console.log('[Smelu Cart] ATC abgefangen — öffne Drawer');

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('is-loading');
      }

      try {
        const formData = new FormData(form);
        // Sections für Refresh-Native-Re-Render
        if (typeof drawer.getSectionsToRender === 'function') {
          const sectionIds = drawer.getSectionsToRender().map(s => s.id).join(',');
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
        } else {
          formData.append('sections', 'cart-drawer,cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }

        const r = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
        });

        if (!r.ok) throw new Error('Add request failed');
        const result = await r.json();

        // Refresh-Native renderContents() öffnet auch automatisch den Drawer
        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents(result);
        } else if (typeof drawer.open === 'function') {
          drawer.open();
        }

        // Bars updaten
        window.dispatchEvent(new CustomEvent('smelu:cart:updated'));
      } catch (err) {
        console.warn('Smelu ATC intercept failed:', err);
        // Last-Resort: nativer Submit, damit User nicht stecken bleibt
        form.removeAttribute('data-smelu-intercepted');
        form.submit();
      } finally {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('is-loading');
        }
      }
  }

  function bindAtcIntercept() {
    // Submit-Event (Native-Form-Submit + dispatched Sticky-ATC)
    document.addEventListener('submit', handleAtcSubmit, true);

    // Click-Fallback für Sticky-ATC (falls Synthetic-Submit nicht greift)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pdp-sticky-atc]');
      if (!btn || btn.disabled) return;
      // Finde zugehörige Form
      const form = document.querySelector('[data-pdp-form]')
        || document.querySelector('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      handleAtcSubmit({ target: form, preventDefault: function () {} });
    }, true);

    console.log('[Smelu Cart] ATC-Intercept aktiv');
  }

  // ===== Wheel-Redirect: Maus-Scroll IMMER = Drawer scrollt =====
  // Wenn Drawer offen ist, wird JEDES Wheel-Event auf den Cart-Items-Bereich
  // umgeleitet — egal wo der Cursor ist (Backdrop, Drawer-Inhalt, Cross-Sell).
  // Ein einheitliches Scroll-Verhalten, keine Cursor-abhängigen Sonderfälle.
  function bindWheelRedirect() {
    document.addEventListener('wheel', (e) => {
      const drawer = document.querySelector('cart-drawer.active');
      if (!drawer) return;
      const scrollable = drawer.querySelector('cart-drawer-items');
      if (!scrollable) return;
      // Default-Scroll überall blocken, manuell den Cart scrollen
      e.preventDefault();
      scrollable.scrollTop += e.deltaY;
    }, { passive: false });
  }

  // ===== Body-Scroll-Lock (iOS-Safari-tauglich) =====
  // Refresh setzt body.overflow-hidden bei Drawer-Open — auf iOS Safari
  // blockt das aber Touch-Scroll nicht. Wir speichern Scroll-Position,
  // setzen body.position=fixed + top=-scrollY, und stellen das bei Close
  // wieder her. Damit kein Background-Scroll mehr.
  let smeluScrollY = 0;
  let smeluLocked = false;
  function smeluUpdateBodyLock() {
    const shouldLock = document.body.classList.contains('overflow-hidden');
    if (shouldLock && !smeluLocked) {
      smeluScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = '-' + smeluScrollY + 'px';
      smeluLocked = true;
    } else if (!shouldLock && smeluLocked) {
      document.body.style.top = '';
      window.scrollTo(0, smeluScrollY);
      smeluLocked = false;
    }
  }

  // ===== Init =====
  // WICHTIG: Bars werden bereits server-seitig korrekt von Liquid gerendert
  // (auf jeden Cart-Update). JS muss NICHT re-syncen — sonst Race-Conditions
  // mit Refresh-Native-cart.js und potenzielle Infinite-Loops via Observer.
  function init() {
    bindCrossSellCards();

    // ATC-Intercept nur EINMAL global binden (document-level)
    if (!window._smeluAtcBound) {
      bindAtcIntercept();
      window._smeluAtcBound = true;
    }

    // Wheel-Redirect nur EINMAL global binden
    if (!window._smeluWheelBound) {
      bindWheelRedirect();
      window._smeluWheelBound = true;
    }

    // Body-Scroll-Lock-Observer (nur einmal global)
    if (!window._smeluBodyLockBound) {
      const bodyObserver = new MutationObserver(smeluUpdateBodyLock);
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      window._smeluBodyLockBound = true;
    }

    // Nach jeder Drawer-DOM-Mutation: nur neue Cross-Sell-Karten verdrahten.
    // KEIN syncBars() hier — Bars sind im neuen Markup bereits korrekt.
    const observer = new MutationObserver(() => {
      bindCrossSellCards();
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
    }

    // Externe Trigger (PDP-ATC, falls Refresh-Native nicht re-rendert)
    window.addEventListener('smelu:cart:updated', () => {
      // Nur als Last-Resort wenn Markup nicht re-rendert wurde
      bindCrossSellCards();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
